import type {
  AdminUserDetail,
  UserGymMembership,
  UserGymTrainerRole,
} from '@steado/admin-contract/user-detail';
import { Link } from '@tanstack/react-router';

import { OwnerBadge } from '@/components/panel/OwnerBadge';
import { FieldRow, FieldRows, Panel } from '@/components/panel/panels';
import { BadgeCell } from '@/components/table/cells';
import { formatDate, formatLocalDate } from '@/lib/format';

const linkClass = 'font-medium text-foreground hover:text-primary';

function GymLink({ gym }: { readonly gym: { readonly id: string; readonly name: string } }) {
  return (
    <Link to="/gyms/$gymId" params={{ gymId: gym.id }} className={linkClass}>
      {gym.name}
    </Link>
  );
}

function TrainerName({ trainer }: { readonly trainer: UserGymMembership['trainers'][number] }) {
  if (trainer.userId === null) return trainer.displayName;
  return (
    <Link
      to="/users/$userId"
      params={{ userId: trainer.userId }}
      search={{ tab: 'profile' }}
      className={linkClass}
    >
      {trainer.displayName}
    </Link>
  );
}

function Membership({ membership }: { readonly membership: UserGymMembership }) {
  const { trainers } = membership;
  const ended =
    membership.endedAt === null
      ? null
      : `${formatDate(membership.endedAt)}${membership.endedBy === null ? '' : `, by the ${membership.endedBy}`}`;
  return (
    <FieldRows>
      <FieldRow
        label="Coached at"
        value={
          <span className="flex items-center gap-2">
            <GymLink gym={membership.gym} />
            <BadgeCell label={membership.status} tone={membership.status === 'active' ? 'ok' : 'mute'} dot />
          </span>
        }
      />
      <FieldRow
        label={trainers.length > 1 ? 'Trainers' : 'Trainer'}
        value={
          trainers.length === 0 ? (
            'None'
          ) : (
            <span className="flex flex-wrap gap-x-3 gap-y-1">
              {trainers.map((trainer) => (
                <TrainerName key={trainer.staffId} trainer={trainer} />
              ))}
            </span>
          )
        }
        {...(trainers.some((t) => t.status === 'active') ? {} : ({ tone: 'warn' } as const))}
      />
      <FieldRow label="Joined" value={formatLocalDate(membership.joinedOn)} />
      <FieldRow
        label="First plan"
        value={membership.firstPlanOn === null ? 'Not yet' : formatLocalDate(membership.firstPlanOn)}
      />
      <FieldRow label="Sharing with the trainers" value={membership.sharing ? 'Yes' : 'No'} />
      {ended === null ? null : <FieldRow label="Ended" value={ended} tone="muted" />}
    </FieldRows>
  );
}

function TrainerRole({ role }: { readonly role: UserGymTrainerRole }) {
  return (
    <FieldRows>
      <FieldRow
        label="Trains at"
        value={
          <span className="flex items-center gap-2">
            <GymLink gym={role.gym} />
            <BadgeCell label={role.status} tone={role.status === 'active' ? 'ok' : 'mute'} dot />
          </span>
        }
      />
      <FieldRow label="Members now" value={String(role.activeMembers)} mono />
      <FieldRow
        label={role.removedAt === null ? 'Since' : 'Removed'}
        value={
          role.removedAt !== null
            ? formatDate(role.removedAt)
            : role.acceptedAt === null
              ? null
              : formatDate(role.acceptedAt)
        }
      />
    </FieldRows>
  );
}

/**
 * Where the user is coached, and where they coach, in Steado for Gyms. Facts
 * about the coaching only; nothing a trainer wrote. Absent for anyone with
 * no gym rows, which is most users.
 */
export function GymPanel({ detail }: { readonly detail: AdminUserDetail }) {
  const { memberships, trainerRoles } = detail.gyms;
  if (memberships.length === 0 && trainerRoles.length === 0) return null;
  return (
    <Panel title="Gym coaching" owner={<OwnerBadge>From Steado for Gyms</OwnerBadge>} bleed>
      <div className="flex flex-col">
        {memberships.map((membership) => (
          <Membership key={membership.membershipId} membership={membership} />
        ))}
        {trainerRoles.map((role) => (
          <TrainerRole key={role.staffId} role={role} />
        ))}
      </div>
    </Panel>
  );
}
